import { useQuery } from "@tanstack/react-query";
import type { Option } from "@/components/ui/multi-select";
import { supabase } from "@/integrations/supabase/client";
import {
  getAllKnownCategoryOptions,
  normalizeCategoryList,
  rememberCustomCategory,
} from "@/lib/category-config";

function collectRowCategories(row: any): string[] {
  return normalizeCategoryList([
    row?.category,
    ...(Array.isArray(row?.categories) ? row.categories : []),
  ]);
}

async function fetchCategoriesFromTable(
  table: "videos" | "audios" | "series",
): Promise<string[]> {
  const { data, error } = await supabase
    .from(table as any)
    .select("*")
    .limit(5000);

  if (error) {
    console.warn(`Failed to fetch ${table} categories`, error.message);
    return [];
  }

  const values = new Set<string>();
  for (const row of data || []) {
    for (const value of collectRowCategories(row)) {
      values.add(value);
    }
  }
  return Array.from(values);
}

export function useCategoryOptions() {
  return useQuery({
    queryKey: ["category-options"],
    staleTime: 60_000,
    queryFn: async () => {
      const [videoValues, audioValues, seriesValues] = await Promise.all([
        fetchCategoriesFromTable("videos"),
        fetchCategoriesFromTable("audios"),
        fetchCategoriesFromTable("series"),
      ]);

      return getAllKnownCategoryOptions([
        ...videoValues,
        ...audioValues,
        ...seriesValues,
      ]);
    },
    initialData: () => getAllKnownCategoryOptions(),
  });
}

export function addCustomCategoryOption(input: string): Option | null {
  const remembered = rememberCustomCategory(input);
  if (!remembered) return null;
  return remembered;
}
